import React from 'react';
import { Button } from '../../ui/common';
import {
  openSettingsActionLabel,
  sourceConnectionConfig,
  sourceCredentialsRejectedTitle,
  sourceNotConnectedTitle,
  type ConnectableSource,
  type SourceConnectionConfig,
} from '../../domain/sourceConnection';
import { ProviderIcon } from '../settings/ProviderIcon';
import { openSettingsSection } from '../settings/settingsDeepLink';

export interface SourceConnectionPanelProps {
  source: ConnectableSource;
  /** Per-screen overrides; everything else comes from the source registry. */
  overrides?: Partial<Pick<SourceConnectionConfig, 'label' | 'description'>>;
  /**
   * `not_connected` (default): no credential stored. `rejected`: the provider
   * refused the stored credential (expired / revoked); `detail` carries its wording.
   */
  variant?: 'not_connected' | 'rejected';
  detail?: string | null;
  onRetry?: () => void;
  isRetrying?: boolean;
}

/**
 * The one connection surface every provider feed renders when its source is
 * not connected. Reads identity and copy from `SOURCE_CONNECTION_REGISTRY`,
 * so a new source only needs a registry entry to get this screen.
 */
export const SourceConnectionPanel: React.FC<SourceConnectionPanelProps> = ({
  source,
  overrides,
  variant = 'not_connected',
  detail = null,
  onRetry,
  isRetrying = false,
}) => {
  const config = { ...sourceConnectionConfig(source), ...overrides };
  const rejected = variant === 'rejected';
  const title = rejected ? sourceCredentialsRejectedTitle(source) : sourceNotConnectedTitle(source);
  return (
    <section
      data-testid="source-connection-panel"
      data-source={source}
      data-variant={variant}
      role={rejected ? 'alert' : 'status'}
      aria-label={title}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        padding: '40px 24px',
        borderRadius: 18,
        background: 'var(--z-surface)',
        color: 'var(--z-text)',
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 52,
          height: 52,
          borderRadius: 16,
          background: rejected ? 'var(--z-bad-bg)' : 'var(--z-accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ProviderIcon name={source} />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 440 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
        {rejected ? (
          <>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--z-muted)' }}>
              {config.label} respondió que el token guardado ya no es válido: expiró o fue revocado.
              Genera uno nuevo y guárdalo en Settings → {config.label}.
            </p>
            {detail ? (
              <p
                data-testid="source-connection-detail"
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: 'var(--z-dim)',
                  fontFamily: "'JetBrains Mono', monospace",
                  overflowWrap: 'anywhere',
                }}
              >
                {detail}
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--z-muted)' }}>{config.description}</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--z-dim)' }}>
              Conéctalo desde Settings → {config.label} para empezar a ver {config.noun}.
            </p>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => openSettingsSection(config.settingsSection)}
        >
          {rejected ? `Actualizar token de ${config.label}` : openSettingsActionLabel(source)}
        </Button>
        {onRetry ? (
          <Button variant="ghost" size="sm" onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? 'Comprobando…' : rejected ? 'Ya lo actualicé' : 'Ya lo conecté'}
          </Button>
        ) : null}
      </div>
    </section>
  );
};
